import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { ref, onValue, query, orderByChild } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import InchargeLayout from './InchargeLayout';
import { toast } from 'react-toastify';
import { FaArrowLeft } from 'react-icons/fa';

const DrawLotteryList = () => {
    const navigate = useNavigate();
    const [lotteryDates, setLotteryDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [lotteryResults, setLotteryResults] = useState([]);
    const [loading, setLoading] = useState(false);

    // Fetch available lottery dates from generatework
    useEffect(() => {
        const datesRef = ref(db, 'ekamwatapzp/generatework');
        onValue(datesRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const dates = Object.keys(data);
                // Filter unique and sort
                const uniqueDates = [...new Set(dates)].sort((a, b) => {
                    // Assuming DD-MM-YYYY format for sort
                    const [d1, m1, y1] = a.split('-');
                    const [d2, m2, y2] = b.split('-');
                    return new Date(`${y2}-${m2}-${d2}`) - new Date(`${y1}-${m1}-${d1}`);
                });
                setLotteryDates(uniqueDates);
                if (uniqueDates.length > 0) setSelectedDate(uniqueDates[0]);
            } else {
                setLotteryDates([]);
            }
        });
    }, []);

    const fetchResults = () => {
        if (!selectedDate) {
            toast.error("कृपया तारीख निवडा.");
            return;
        }

        setLoading(true);
        // User wants to fetch from generatework. The assigned winners are in EngineerRegistrations.
        // We'll fetch EngineerRegistrations and filter those who have a GeneratedLottery for this date.
        const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        onValue(engRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const matchedResults = [];

                Object.keys(data).forEach(key => {
                    const user = data[key];
                    if (user && user.GeneratedLottery && user.GeneratedLottery[selectedDate]) {
                        const lotteryInfo = user.GeneratedLottery[selectedDate];
                        matchedResults.push({
                            id: key,
                            applicantName: user.firstName ? `${user.firstName} ${user.surname}` : user.fullName || "User",
                            phoneNo: user.whatsappNo || user.mobile || "-",
                            taluka: user.taluka || "-",
                            workName: lotteryInfo.workName || lotteryInfo.name || "-",
                            amount: lotteryInfo.amount || lotteryInfo.tenderAmount || "0.00"
                        });
                    }
                });

                setLotteryResults(matchedResults);
            } else {
                setLotteryResults([]);
            }
            setLoading(false);
        });
    };

    // Auto fetch on initial date set
    useEffect(() => {
        if (selectedDate) fetchResults();
    }, [selectedDate]);

    return (
        <InchargeLayout>
            <div className="min-h-screen bg-gray-50 flex flex-col ">
                {/* Header Section */}
                <div className="px-6 py-6 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-200">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => navigate(-1)}
                            className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-500 hover:text-indigo-900 transition active:scale-95 flex items-center justify-center"
                        >
                            <FaArrowLeft />
                        </button>
                        <h1 className="text-xl font-bold text-indigo-900 tracking-tight">सु.बे. लॉटरी यादी</h1>
                    </div>
                    <div className="flex items-center text-sm font-medium text-gray-500 gap-2">
                        <span>सांगली जिल्हा परिषद</span>
                        <span>/</span>
                        <span className="text-gray-600 font-semibold">सु.बे. लॉटरी यादी</span>
                    </div>
                </div>

                <div className="p-6">
                    <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-sm border border-gray-200 p-8">

                        {/* Filter Section */}
                        <div className="mb-8 p-6 bg-white border-b border-gray-100 flex flex-col gap-3">
                            <label className="text-sm font-bold text-gray-700">
                                लॉटरीची तारीख निवडा <span className="text-red-500">*</span>
                            </label>
                            <div className="flex items-center gap-4">
                                <select
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="w-full md:w-64 border border-gray-300 rounded px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500"
                                >
                                    <option value="">Select Date</option>
                                    {lotteryDates.map((date, idx) => (
                                        <option key={idx} value={date}>{date}</option>
                                    ))}
                                </select>
                                <button
                                    onClick={fetchResults}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded text-sm font-semibold transition shadow-md"
                                >
                                    Search
                                </button>
                            </div>
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto border border-gray-200 rounded">
                            <table className="w-full text-left text-sm border-collapse">
                                <thead>
                                    <tr className="bg-white text-gray-700 font-bold border-b border-gray-200">
                                        <th className="px-6 py-4 border-r border-gray-200 w-16 text-center">#</th>
                                        <th className="px-6 py-4 border-r border-gray-200 min-w-[200px]">अर्जदाराचे नाव</th>
                                        <th className="px-6 py-4 border-r border-gray-200">फोन नंबर</th>
                                        <th className="px-6 py-4 border-r border-gray-200">तालुका</th>
                                        <th className="px-6 py-4 min-w-[350px]">कामाचे नाव</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {loading ? (
                                        <tr><td colSpan="5" className="text-center py-10 text-gray-400">Loading Results...</td></tr>
                                    ) : lotteryResults.length > 0 ? (
                                        lotteryResults.map((item, index) => (
                                            <tr key={item.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-indigo-50/30 transition-colors`}>
                                                <td className="px-6 py-4 border-r border-gray-200 text-center font-medium">{index + 1}</td>
                                                <td className="px-6 py-4 border-r border-gray-200 font-medium text-gray-800">
                                                    {item.winnerName || item.applicantName || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 border-r border-gray-200 text-gray-600">
                                                    {item.winnerPhone || item.phoneNo || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 border-r border-gray-200 text-gray-700">
                                                    {item.taluka || item.winnerTaluka || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-gray-800 leading-relaxed font-medium">
                                                            {item.workName || 'Work Name Not Found'}
                                                        </span>
                                                        <span className="text-gray-500 font-bold text-xs">
                                                            Rs. - {item.amount || '0.00'}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-12 text-center text-gray-500 font-medium">
                                                No data available in table
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                    </div>
                </div>
            </div>
        </InchargeLayout>
    );
};

export default DrawLotteryList;
